import { useRef, useState } from 'react';
import styled from 'styled-components';

import { Flex, Space } from '@/components/base';
import ContentPreview from '@/components/Preview/ContentPreview';
import { MainText } from '@/components/Typography/NamedTypo';
import Typo from '@/components/Typography/Typo';
import { NOT_ASSIGNED_LABEL } from '@/configs/reserved-word';
import useAgentStore from '@/features/Agent/stores';
import useColor from '@/hooks/domain/useColor';
import useFixed from '@/hooks/pure/useFixed';

type Props = {
  alias: string;
};
const AgentItem = ({ alias }: Props) => {
  const timeout = useRef<NodeJS.Timer | null>(null);
  const ref = useRef(null);
  const color = useColor();
  const [previewOpen, setPreviewOpen] = useState(false);
  const agentMap = useAgentStore((state) => state.agentMap);

  const pos = useFixed(ref, {
    reflect: {
      x: true,
      y: true,
    },
    offset: {
      y: 0,
    },
  });

  // 300ms 뒤에 API 요청
  const handleMouseEnter = () => {
    timeout.current = setTimeout(() => {
      setPreviewOpen(true);
    }, 300);
  };

  const handleMouseLeave = () => {
    setPreviewOpen(false);
    if (!timeout.current) return;
    clearTimeout(timeout.current);
  };

  return (
    <>
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        ref={ref}
      >
        <MainText>{agentMap[alias]?.name || NOT_ASSIGNED_LABEL}</MainText>
      </div>

      {previewOpen && alias && agentMap[alias] && (
        <ContentPreview style={pos}>
          <Style>
            <Typo type="body2" weight="sb">
              {agentMap[alias]?.name}
            </Typo>
            <Space h="16px" />
            <Flex g="8px" w="wrap">
              {agentMap[alias]?.role && (
                <ItemWrapper g="6px">
                  <Typo type="body3" color={color.gray600}>
                    Role
                  </Typo>
                  <Typo type="body3" color={color.gray800}>
                    {agentMap[alias]?.role}
                  </Typo>
                </ItemWrapper>
              )}
              {agentMap[alias]?.email && (
                <ItemWrapper g="6px">
                  <Typo type="body3" color={color.gray600}>
                    Email
                  </Typo>
                  <Typo type="body3" color={color.gray800}>
                    {agentMap[alias]?.email}
                  </Typo>
                </ItemWrapper>
              )}
            </Flex>
          </Style>
        </ContentPreview>
      )}
    </>
  );
};

export default AgentItem;

const Style = styled.section`
  width: 400px;
  min-height: 120px;
  padding: 24px 32px;
  overflow: hidden;
`;

const ItemWrapper = styled(Flex)`
  border: 1px solid ${(props) => props.theme.colors.gray400};
  border-radius: 5px;
  padding: 8px 10px;
`;
